import { UpdatePage } from '@components/molecules';
import {
  AutocompleteElement,
  SwitchElement,
  TextFieldElement,
  useForm,
  UseFormSetValue,
} from 'react-hook-form-mui';
import API from '@apps/nnc/services/cause';
import { ModelProps } from '@/types';
import { CauseSchema, CauseSchemaType } from '@apps/nnc/schemas/cause';
import { Fragment, JSX } from 'react';
import { Grid, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { setDefaultLanguageValues } from '@apps/localization/services/utils';
import { LANGUAGES_AVAILABLE } from '@/const';
import AsyncAutocompleteElement from '@/components/atoms/asyncautocomplete';
import UnitAPI from '@apps/localization/services/unit';
import OriginAPI from '@apps/nnc/services/origin';

export default function ObjectUpdatePage(modelProps: ModelProps): JSX.Element {
  const { t } = useTranslation();
  const defaultValues = {
    translations: LANGUAGES_AVAILABLE.map((lang, idx) => ({
      language_code: {
        id: lang.id,
        label: lang.label,
      },
      inlineIndex: idx,
    })),
    order: 0,
    is_active: true,
  };
  const formContext = useForm<CauseSchemaType>({
    resolver: zodResolver(CauseSchema),
    defaultValues: defaultValues,
  });

  const handleSelectAllUnits = async () => {
    try {
      const response = await UnitAPI.select({});
      const allUnits = response.data.results || response.data || response;
      formContext.setValue('units', allUnits);
    } catch (error) {
      console.error('Error loading all units:', error);
    }
  };

  return (
    <UpdatePage
      schema={CauseSchema}
      api={API}
      title={t('Cause')}
      modelProps={modelProps}
      formcontext={formContext}
      setChildValues={(setValue) => {
        setDefaultLanguageValues(setValue as UseFormSetValue<CauseSchemaType>);
      }}
      canSaveAndContinue
      keepValues={['units', 'origin', 'order']}
    >
      <>
        <Grid container sx={{ padding: 2 }} spacing={2}>
          {LANGUAGES_AVAILABLE.map((_item, idx) => (
            <Fragment key={idx}>
              <TextFieldElement
                name={`translations.${idx}.inlineIndex`}
                value={idx}
                type="hidden"
                sx={{ display: 'none' }}
              />

              <Grid size={{ xs: 12, md: 6 }}>
                <AutocompleteElement
                  name={`translations.${idx}.language_code`}
                  label={t('Language')}
                  options={LANGUAGES_AVAILABLE}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextFieldElement
                  name={`translations.${idx}.name`}
                  label={t('Name')}
                  fullWidth
                  required
                />
              </Grid>
            </Fragment>
          ))}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextFieldElement
              name="order"
              label={t('Order')}
              fullWidth
              type="number"
              required
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <AsyncAutocompleteElement
              name="units"
              label={t('Unit')}
              api={UnitAPI.select}
              required
              multiple
            />
          </Grid>
          <Grid size={{ xs: 12, md: 1 }}>
            <Button
              variant="contained"
              onClick={handleSelectAllUnits}
              fullWidth
              sx={{ height: '56px' }}
            >
              {t('Select All')}
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 9 }}>
            <AsyncAutocompleteElement
              name="origin"
              label={t('Origin')}
              api={() => OriginAPI.select({ is_active: 1 })}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <SwitchElement label={t('Is Active?')} name="is_active" />
          </Grid>
        </Grid>
      </>
    </UpdatePage>
  );
}
